import React, { FC, useRef, useState } from 'react'
import styled from 'styled-components'
import { Dialog, DialogContent, Button, IconButton, Table, TableHead, TableBody, TableRow, TableCell, TableContainer } from '@material-ui/core'
import { Close, Add } from '@material-ui/icons'
import { Image, Spinner } from 'ui'
import * as XLSX from 'xlsx'
import { importUsers, ImportUserRow, RowStatus } from './mockImportUsersApi'
import Import from 'assets/icons/Import.svg'

// --------------- Styled components ---------------

const Header = styled.div`
  display: flex;
  padding: 16px 24px;
  justify-content: space-between;
  align-items: center;
  border-bottom: 0.909px solid #E2E8F0;
  background: rgba(241, 245, 249, 0.30);
`
const Title = styled.div`
  color: #0F172A;
  font-family: Rubik;
  font-size: 20px;
  font-weight: 500;
  line-height: 30px;
`
const StyledClose = styled(Close)`
  color: #000;
  font-size: 20px;
`
const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
`
const DropZone = styled.div<{ active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px;
  border-radius: 8px;
  border: 2px dashed ${p => p.active ? '#2563EB' : '#CBD5E1'};
  background: ${p => p.active ? '#EFF6FF' : '#F8FAFC'};
  cursor: pointer;
  transition: all 0.15s;
`
const DropZoneText = styled.div`
  color: #64748B;
  font-family: Rubik;
  font-size: 14px;
  font-weight: 400;
  text-align: center;
`
const DropZoneHint = styled.div`
  color: #94A3B8;
  font-family: Rubik;
  font-size: 12px;
`
const BrowseLink = styled.span`
  color: #2563EB;
  cursor: pointer;
  text-decoration: underline;
`
const PreviewTitle = styled.div`
  color: #0F172A;
  font-family: Rubik;
  font-size: 14px;
  font-weight: 600;
`
const PreviewCount = styled.span`
  color: #64748B;
  font-weight: 400;
  margin-right: 6px;
`
const TableContainerStyled = styled(TableContainer)`
  max-height: 360px;
  border-radius: 8px;
  border: 0.909px solid #E2E8F0;
`
const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 4px;
`
const SaveButton = styled(Button)`
  display: flex;
  padding: 8px 24px;
  border-radius: 8px;
  background: #2563EB;
  color: #FFF;
  font-family: Rubik;
  font-size: 15px;
  font-weight: 500;
`
const CancelButton = styled(Button)`
  display: flex;
  padding: 8px 24px;
  border-radius: 8px;
  border: 0.909px solid #D1D5DC;
  background: #FFF;
  color: #364153;
  font-family: Rubik;
  font-size: 15px;
  font-weight: 500;
`
const ErrorText = styled.div`
  color: #DC2626;
  font-family: Rubik;
  font-size: 13px;
`
const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`
const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: #E2E8F0;
  overflow: hidden;
`
const ProgressFill = styled.div<{ pct: number }>`
  height: 100%;
  border-radius: 999px;
  background: #2563EB;
  width: ${p => p.pct}%;
  transition: width 0.2s ease;
`
const ProgressLabel = styled.div`
  color: #64748B;
  font-family: Rubik;
  font-size: 13px;
  text-align: left;
`
const StatusBadge = styled.div<{ status: RowStatus }>`
  display: inline-flex;
  padding: 2px 10px;
  font-family: Rubik;
  font-size: 12px;
  font-weight: 500;
  background: ${p => p.status === 'success' ? '#DCFCE7' : '#FEE2E2'};
  color: ${p => p.status === 'success' ? '#16A34A' : '#DC2626'};
`
const ImportIconImage = styled(Image)`
  width: 32px;
  height: 32px;
`

// --------------- Column mapping ---------------
// Headers row is row index 2 in the xlsx (0-indexed)
// Strip trailing * and trim before matching

const HEADER_DATA_START_ROW = 3 // first real data row (0-indexed)
const HEADER_ROW_INDEX = 2

const COLUMN_MAP: Record<string, keyof ImportUserRow> = {
  'שם פרטי':                          'givenName',
  'שם משפחה':                         'familyName',
  'שם נעורים':                         'middleName',
  'כינוי':                             'nickName',
  'תאריך לידה':                        'dateOfBirth',
  'תעודת זהות':                        'israeliId',
  'מין':                               'gender',
  'מצב משפחתי':                       'maritalStatus',
  'טלפון נייד':                        'mainMobilePhone',
  'דואר אלקטרוני':                     'email',
  'ישוב':                              'addressCity',
  'רחוב':                              'addressStreet',
  'מספר בית':                          'addressHouseNumber',
  'ממ"ד/מקלט':                         'shelter',
  'איש קשר במקרה אסון':               'contactPerson1',
  'טלפון איש קשר במקרה אסון':         'contactPersonCellPhone1',
  'איש קשר 2 במקרה אסון':             'contactPerson2',
  'טלפון איש קשר 2 במקרה אסון':       'contactPersonCellPhone2',
  'קבוצות':                            'groups',
  'סוג משתמש':                         'userType',
  'תפקיד בצח"י':                       'roleInCommunity',
  'בן/בת זוג':                         'spouseId',
  'ת.ז. בן/בת זוג':                    'spouseIdentityNumber',
  'שם בן/בת זוג':                      'spouseName',
}

const parseXlsx = (buffer: ArrayBuffer): ImportUserRow[] => {
  const wb = XLSX.read(buffer, { type: 'array' })
  const ws = wb.Sheets[wb.SheetNames[0]]
  const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1 })

  const headerRow: string[] = (rows[HEADER_ROW_INDEX] || []).map((h: any) =>
    String(h ?? '').replace(/\*/g, '').trim()
  )

  const colIndex: Partial<Record<keyof ImportUserRow, number>> = {}
  headerRow.forEach((h, i) => {
    const field = COLUMN_MAP[h]
    if (field) colIndex[field] = i
  })

  return rows
    .slice(HEADER_DATA_START_ROW)
    .filter(row => row.some(cell => cell != null && cell !== ''))
    .map(row => {
      const user: any = {}
      for (const [field, idx] of Object.entries(colIndex)) {
        const raw = row[idx as number]
        user[field] = raw != null ? String(raw).trim() : ''
      }
      return user as ImportUserRow
    })
}

// --------------- Component ---------------

type Props = {
  open: boolean
  onClose: () => void
  communityId: number
  onImported: () => void
}

const ImportUsersModal: FC<Props> = ({ open, onClose, communityId, onImported }) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [rows, setRows] = useState<ImportUserRow[]>([])
  const [dragOver, setDragOver] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [progress, setProgress] = useState({ imported: 0, total: 0 })
  const [rowStatuses, setRowStatuses] = useState<Record<number, { status: RowStatus; message?: string }>>({})

  const reset = () => {
    setRows([])
    setError(null)
    setSaving(false)
    setProgress({ imported: 0, total: 0 })
    setRowStatuses({})
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const processFile = (file: File) => {
    setError(null)
    const reader = new FileReader()
    reader.onload = e => {
      try {
        const parsed = parseXlsx(e.target!.result as ArrayBuffer)
        if (parsed.length === 0) {
          setError('לא נמצאו שורות לייבוא בקובץ')
          return
        }
        setRows(parsed)
      } catch {
        setError('שגיאה בקריאת הקובץ — ודא שהקובץ בפורמט xls/xlsx')
      }
    }
    reader.readAsArrayBuffer(file)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) processFile(file)
    e.target.value = ''
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) processFile(file)
  }

  const handleImport = async () => {
    setSaving(true)
    setError(null)
    setProgress({ imported: 0, total: rows.length })
    setRowStatuses({})
    try {
      await importUsers(rows, communityId, (index, total, status, message) => {
        setRowStatuses(prev => ({ ...prev, [index]: { status, message } }))
        setProgress(prev => ({ ...prev, imported: prev.imported + 1, total }))
      })
      //reset()
      //onClose()
      //onImported()

       setSaving(false)
    } catch {
      setError('שגיאה בייבוא — נסה שוב')
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} fullWidth maxWidth="md" scroll="body">
      <Header>
        <Title>ייבוא משתמשים מקובץ</Title>
        <IconButton onClick={handleClose}><StyledClose /></IconButton>
      </Header>

      <DialogContent>
        <Body>
          {rows.length === 0 && (
            <DropZone
              active={dragOver}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <ImportIconImage src={Import} />
              <DropZoneText>
                גרור קובץ לכאן, או <BrowseLink>בחר קובץ</BrowseLink>
              </DropZoneText>
              <DropZoneHint>פורמטים נתמכים: .xls, .xlsx</DropZoneHint>
            </DropZone>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".xls,.xlsx"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />

          {error && <ErrorText>{error}</ErrorText>}

          {saving && (
            <ProgressContainer>
              <ProgressBar>
                <ProgressFill pct={progress.total > 0 ? Math.round((progress.imported / progress.total) * 100) : 0} />
              </ProgressBar>
              <ProgressLabel>
                {progress.imported} / {progress.total} ({progress.total > 0 ? Math.round((progress.imported / progress.total) * 100) : 0}%)
              </ProgressLabel>
            </ProgressContainer>
          )}

          {rows.length > 0 && (
            <>
              <PreviewTitle>
                תצוגה מקדימה
                <PreviewCount>({rows.length} שורות)</PreviewCount>
              </PreviewTitle>
              <TableContainerStyled>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>שם פרטי</TableCell>
                      <TableCell>שם משפחה</TableCell>
                      <TableCell>ת.ז.</TableCell>
                      <TableCell>נייד</TableCell>
                      <TableCell>דוא"ל</TableCell>
                      <TableCell>ישוב</TableCell>
                      <TableCell>תאריך לידה</TableCell>
                      <TableCell>סטטוס</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, i) => (
                      <TableRow key={i}>
                        <TableCell>{row.givenName}</TableCell>
                        <TableCell>{row.familyName}</TableCell>
                        <TableCell>{row.israeliId}</TableCell>
                        <TableCell>{row.mainMobilePhone}</TableCell>
                        <TableCell>{row.email}</TableCell>
                        <TableCell>{row.addressCity}</TableCell>
                        <TableCell>{row.dateOfBirth}</TableCell>
                         <TableCell>
                          {rowStatuses[i]?.status === 'success' && <StatusBadge status="success">יובא</StatusBadge>}
                          {rowStatuses[i]?.status === 'error' && <StatusBadge status="error">{rowStatuses[i].message || 'שגיאה'}</StatusBadge>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainerStyled>
            </>
          )}

          <Actions>
            <CancelButton onClick={handleClose} disabled={saving}>ביטול</CancelButton>
            {rows.length > 0 && (
              <SaveButton onClick={handleImport} disabled={saving}>
                {saving ? <Spinner size={20} /> : `ייבוא ${rows.length} משתמשים`}
              </SaveButton>
            )}
          </Actions>
        </Body>
      </DialogContent>
    </Dialog>
  )
}

export default ImportUsersModal
